import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { assignChainAtomic, bundleErrorMessage } from '../services/reservationBundles';
import { groupReasons, coverageNote, REMEDY_LABEL } from '../services/reservationReasons';
import toast from 'react-hot-toast';

// RESERVIERUNGSKETTE-001: verständliche Gründe für Antworten ohne eigene Server-Meldung.
const SINGLE_ERROR_TEXT = Object.freeze({
  reservation_driver_client_required: 'Die Fahrer-App dieses Fahrers kann die Reservierung nicht übernehmen (abgemeldet oder zu alt). Bitte App aktualisieren und anmelden lassen.',
  reservation_not_reachable: 'Der Fahrer schafft es zeitlich nicht bis zur Abholung.',
  reservation_hard_gate: 'Ab zehn Minuten vor dem Termin ist keine neue Zuweisung mehr möglich – die Fahrt wird jetzt live vermittelt.',
  driver_not_eligible: 'Fahrzeug/Kategorie oder Zahlungsart des Fahrers passt nicht zu dieser Reservierung.',
  reservation_chain_conflict: 'Der Fahrer hat zur selben Zeit schon eine andere Reservierung.',
  customer_blocked: 'Dieser Fahrer hat den Fahrgast blockiert.',
  self_order_forbidden: 'Der Fahrer ist selbst der Besteller dieser Reservierung.',
  offer_expired: 'Das Angebot ist abgelaufen. Bitte Stand neu laden.',
  reservation_state_changed: 'Die Reservierung hat sich gerade geändert. Bitte Stand neu laden und erneut zuweisen.',
  reservation_not_acceptable: 'Die Reservierung ist nicht mehr verfügbar (gestartet, storniert oder anders vergeben).',
  // RESERVIERUNGSKETTE-002: „offen für alle“ — ein anderer Partner oder Fahrer war schneller, bzw. Flotte passt nicht.
  partner_fleet_not_eligible: 'Für diese Reservierung hat deine Flotte keinen passenden Fahrer bzw. kein passendes Fahrzeug.',
  'Dir nicht angeboten': 'Die Reservierung ist dir nicht (mehr) angeboten – ein anderer Partner oder Fahrer hat sie übernommen oder sie wurde neu vergeben.',
});
import { Calendar, Flag, MapPin, User, Users, X } from 'lucide-react';
import { alsDatum, uhrzeit, wochentagKurzMitZeit } from '../lib/zeit';

// ZEIT-HELFER-001: scheduled_for/from/to kommen zwar normalisiert vom Backend, werden hier
// trotzdem NUR über lib/zeit gelesen — ein rohes SQLite-Datum wäre sonst zwei Stunden früh.
const fmtWhen = (iso) => (iso ? `${wochentagKurzMitZeit(iso)} Uhr` : '—');
const euro = (n) => (n != null ? Number(n).toFixed(2).replace('.', ',') + ' €' : '—');
const paymentLabels = new Map([['cash', 'Bar beim Fahrer'], ['card', 'Karte'], ['paypal', 'PayPal'], ['wallet', 'Wallet']]);
export const reservationAddress = (value) => {
  const address = value == null ? '' : String(value).trim();
  return address || '—';
};
export const reservationDistance = (value) => {
  if (value == null || value === '') return null;
  const distance = Number(value);
  if (!Number.isFinite(distance) || distance < 0) return null;
  return `${new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(distance)} km`;
};
// Business commit, push queue and driver acceptance are separate confirmations.
export const reservationDeliveryMessage = (response, fleet = false) => {
  const prefix = fleet ? 'An die Flotte freigegeben' : 'Fahrerangebot gespeichert';
  const delivery = response?.push_delivery;
  if (delivery?.state === 'queued') return `${prefix} – Pushzustellung vorgemerkt, Anzeige unbestätigt – Annahme noch offen`;
  if (delivery?.state === 'no_eligible_recipients') return `${prefix} – kein berechtigter Push-Empfänger – Annahme noch offen`;
  // Older/idempotent responses contain no queue receipt; target counts cannot replace it.
  return `${prefix} – Pushzustellung unbestätigt – Annahme noch offen`;
};
const btn = (color, outline) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  border: outline ? `1px solid ${color}` : 'none', background: outline ? '#fff' : color, color: outline ? color : '#fff',
});
const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 16, marginBottom: 12 };

export default function Reservations() {
  const [offered, setOffered] = useState([]);
  const [offeredBundles, setOfferedBundles] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [acceptedBundles, setAcceptedBundles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [chains, setChains] = useState([]);            // Ketten-Vorschläge (Engine)
  // RESERVIERUNG-GRUND-ZEIGEN-001: der letzte Sammelruf, der niemanden erreicht hat —
  // samt Gründen. Bleibt stehen, bis der Partner ihn wegklickt oder die nächste
  // Zuweisung gelingt; ein Toast wäre für vier Zeilen Erklärung zu kurzlebig.
  const [noReach, setNoReach] = useState(null);
  const [assignChainKey, setAssignChainKey] = useState(null);
  const [assigningChainKey, setAssigningChainKey] = useState(null);
  const assigningChainRequests = useRef(new Set());
  const readGeneration = useRef(0);
  const mounted = useRef(false);
  const refreshConflict=useRef(false);
  const [refreshNotice,setRefreshNotice]=useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = async () => {
    const generation = ++readGeneration.current;
    try {
      const r = await api.get('/partners/reservations');
      if (!mounted.current || generation !== readGeneration.current) return;
      setOffered(r.data.offered || []);
      setOfferedBundles(r.data.offered_bundles || []);
      setAccepted(r.data.accepted || []);
      setAcceptedBundles(r.data.bundles || []);
      setLoadError(null);
      if(refreshConflict.current){refreshConflict.current=false;setActionError(null);setAssignFor(null);setAssignChainKey(null);
        setRefreshNotice('Der aktuelle Angebotsstand wurde geladen. Bitte prüfe ihn und wähle erneut; es wurde nichts automatisch angenommen.');}
      setNow(Date.now());
    } catch {
      if (!mounted.current || generation !== readGeneration.current) return;
      setLoadError('Reservierungen konnten nicht aktualisiert werden. Bitte lade den aktuellen Stand neu.');
    }
    try {
      const c = await api.get('/partners/reservation-chains');
      if (mounted.current && generation === readGeneration.current) setChains((c.data.chains || []).filter(k => k.fahrten >= 2));
    } catch {}
  };
  useEffect(() => {
    mounted.current = true;
    load();
    api.get('/partners/drivers').then(r => setDrivers((r.data.drivers || []).filter(d => d.approval_status === 'approved'))).catch(() => {});
    const t = setInterval(load, 6000);   // live: neue Reservierungen tauchen ohne Refresh auf
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { mounted.current = false; ++readGeneration.current; clearInterval(t); clearInterval(tick); };
  }, []);

  const liveOffer = (iso, at = now) => (alsDatum(iso)?.getTime() ?? 0) > at;
  const reportActionError = (e) => {
    const status = e.response?.status;
    const code = e.response?.data?.error;
    // RESERVIERUNGSKETTE-001: jede Ablehnung mit ihrem echten Grund — nie mehr der Ketten-Text
    // für eine Einzelreservierung.
    const message = (status === 409 || status === 400 || status === 403) && (e.response?.data?.message || SINGLE_ERROR_TEXT[code])
      ? (e.response?.data?.message || SINGLE_ERROR_TEXT[code])
      : status === 409
      ? (bundleErrorMessage(e) || 'Das Angebot oder die Reservierung wurde geändert. Bitte prüfe den aktuellen Stand.')
      : status === 503 ? 'Die Zuweisung kann gerade nicht sicher bestätigt werden. Bitte lade den Stand neu und versuche es später.'
      : !status || status >= 500 ? 'Antwort unbestätigt. Bitte lade den aktuellen Stand, bevor du erneut zuweist.'
      : (e.response?.data?.message || 'Die Aktion konnte nicht bestätigt werden. Bitte prüfe den aktuellen Stand.');
    if(status===409&&['reservation_state_changed','offer_expired','bundle_member_unavailable','reservation_not_acceptable'].includes(e.response?.data?.error))refreshConflict.current=true;
    setActionError(message); toast.error(message); load();
  };
  const startAction = (key) => {
    if (assigningChainRequests.current.has(key)) return false;
    assigningChainRequests.current.add(key); setAssigningChainKey(key);setRefreshNotice(null); return true;
  };
  const endAction = (key) => {
    assigningChainRequests.current.delete(key);
    setAssigningChainKey(current => current === key ? null : current);
  };
  // RESERVIERUNGSKETTE-002: „offen für alle“ hat keine Partnerfrist — annehmbar, bis jemand schneller ist.
  const canAnswer = (ride) => !offered.some(item => item.id === ride.id) || ride.open_market === true
    || liveOffer(ride.partner_offer_expires_at, Date.now());

  const accept = async (ride, driverId) => {
    if (!canAnswer(ride)) { reportActionError({ response: { status: 409 } }); return; }
    const key = `single:${ride.id}`; if (!startAction(key)) return;
    try {
      const r = await api.post(`/partners/reservations/${ride.id}/accept`, {
        ...(driverId ? { driver_id: driverId } : {}),
        ...(ride.reservation_partner_offer_token ? { offer_token: ride.reservation_partner_offer_token } : {}),
      });
      if (r.data?.ok !== true || r.data.assigned !== (driverId ? 'driver' : 'fleet')) throw new Error('unconfirmed_assignment');
      setActionError(null);setRefreshNotice(null);
      if (driverId) {
        toast.success(reservationDeliveryMessage(r.data)); setNoReach(null);
        // RESERVIERUNGSKETTE-001: geschätzte bzw. nicht prüfbare Anfahrt offen nennen (kein Fehler).
        if (r.data?.reachability?.notice) toast(r.data.reachability.notice, { duration: 9000 });
      }
      else {
        const alert = r.data?.fleet_alert;
        const noRecipients = r.data?.push_delivery?.state === 'no_eligible_recipients';
        const eligible = noRecipients ? 0 : (alert?.eligible_driver_count ?? null);
        const alerted = alert?.alerted_driver_count ?? null;
        // Availability is useful for diagnosis, but never evidence of provider/device delivery.
        if (noRecipients || alerted === 0) {
          toast.error(eligible === 0
            ? 'Freigegeben, aber kein Fahrer kommt dafür in Frage — die Gründe stehen oben.'
            : 'Freigegeben, aber kein aktives Fahrergerät verfügbar — Pushzustellung unbestätigt.',
          { duration: 8000 });
          setNoReach({ rideId: ride.id, eligible, alerted, groups: groupReasons(alert),
            note: coverageNote(r.data?.fleet_coverage), fleetDriverCount: alert?.fleet_driver_count ?? null });
        } else { toast.success(reservationDeliveryMessage(r.data, true)); setNoReach(null); }
      }
      setAssignFor(null); load();
    } catch (e) { reportActionError(e); }
    finally { endAction(key); }
  };
  const decline = async (ride) => {
    if (!canAnswer(ride)) { reportActionError({ response: { status: 409 } }); return; }
    const key = `single:${ride.id}`; if (!startAction(key)) return;
    try {
      await api.post(`/partners/reservations/${ride.id}/decline`, ride.reservation_partner_offer_token
        ? { offer_token: ride.reservation_partner_offer_token } : {});
      setActionError(null); toast('Reservierung abgelehnt'); load();
    }
    catch (e) { reportActionError(e); }
    finally { endAction(key); }
  };
  const declineBundle = async (bundle) => {
    if (!liveOffer(bundle.partner_offer_expires_at, Date.now())) { reportActionError({ response: { status: 409 } }); return; }
    const key = `partner-offer:${bundle.bundle_id}`; if (!startAction(key)) return;
    try {
      await api.post(`/partners/reservation-bundles/${bundle.bundle_id}/decline`, {
        expected_state: bundle.state_token,
      });
      setActionError(null); toast('Reservierungskette abgelehnt'); load();
    } catch (e) { reportActionError(e); }
    finally { endAction(key); }
  };
  // Ganze Kette an EINEN Fahrer: exakt ein atomarer Backend-Call, niemals Einzelaccepts.
  const assignChain = async (chain, driverId, requestKey) => {
    const offer = offeredBundles.find(item => requestKey === `partner-offer:${item.bundle_id}`);
    if (offer && !liveOffer(offer.partner_offer_expires_at, Date.now())) { reportActionError({ response: { status: 409 } }); return; }
    if (!startAction(requestKey)) return;
    try {
      const result = await assignChainAtomic(api, {
        rideIds: chain.rides,
        driverId,
        expectedState: chain.state_token,
      });
      if (result?.ok !== true) throw new Error('unconfirmed_assignment');
      const from = fmtWhen(result.from);
      const to = result.to ? `${uhrzeit(result.to)} Uhr` : '—';
      setActionError(null);
      toast.success(`${result.count} Reservierungen: ${reservationDeliveryMessage(result)} · ${from} – ${to} · ${euro(result.total_price)}`);
      setAssignChainKey(null);
      load();
    } catch (e) {
      // Read resync only; never split an atomic bundle or silently retry its write.
      reportActionError({ ...e, response: e.response ? { ...e.response,
        data: { ...e.response.data, message: e.response.data?.message || bundleErrorMessage(e) } } : undefined });
    } finally {
      endAction(requestKey);
    }
  };
  const countdown = (iso) => {
    const ziel = alsDatum(iso);
    const ms = ziel ? ziel.getTime() - now : 0;
    if (!ziel) return 'Frist fehlt – Stand neu laden';
    if (ms <= 0) return 'abgelaufen – Stand neu laden';
    // RESERVIERUNGSKETTE-001: lange Fristen (bis 45 Min. vor Abholung) als Zeitpunkt statt Minutenzähler.
    // RESERVIERUNGSKETTE-002: Formatierung nur über lib/zeit (Zeitvertrag).
    if (ms > 2 * 3600 * 1000) return `bis ${wochentagKurzMitZeit(iso)} Uhr`;
    const s = Math.ceil(ms / 1000); return `noch ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} · bis ${uhrzeit(iso)} Uhr`;
  };
  const driverStatus = (ride) => ride.driver_id ? (ride.driver_name ? `Angenommen: ${ride.driver_name}` : 'Vom Fahrer angenommen')
    : ride.assigned_driver_id ? 'Zugewiesen – wartet auf Fahrer-Annahme'
    : Number(ride.released_to_fleet) === 1 ? 'An alle Fahrer freigegeben – wartet auf Annahme' : 'Bitte einen Fahrer zuweisen';
  const assignmentNote = (ride) => ride.driver_id ? null : ride.assigned_driver_id
    ? <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 8 }}>Fahrerangebot {countdown(ride.driver_offer_expires_at)}</div>
    : Number(ride.released_to_fleet) === 1
      ? <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 8 }}>Keine bestätigte Fahrerannahme oder Ankunftszusage.</div> : null;
  const routeLine = (r) => {
    const distance = reservationDistance(r.distance_km);
    return (
      <div data-reservation-route={r.id} style={{ margin: '10px 0', display: 'grid', gap: 8 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 8, alignItems: 'start' }}>
          <MapPin size={16} color="#2563eb" aria-hidden="true" style={{ marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Abholung</div>
            <div style={{ fontSize: 14, lineHeight: 1.4, color: '#111827', overflowWrap: 'anywhere' }}>
              {reservationAddress(r.pickup_address)}
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 8, alignItems: 'start' }}>
          <Flag size={16} color="#82468c" aria-hidden="true" style={{ marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Ziel</div>
            <div style={{ fontSize: 14, lineHeight: 1.4, color: '#111827', overflowWrap: 'anywhere' }}>
              {reservationAddress(r.dropoff_address)}
            </div>
          </div>
        </div>
        {distance && (
          <div data-reservation-distance={r.id} style={{ fontSize: 13, fontWeight: 700, color: '#4b5563', paddingLeft: 28 }}>
            Strecke · {distance}
          </div>
        )}
      </div>
    );
  };
  const acceptedSingles = accepted.filter((ride) => !ride.reservation_bundle_id);
  // RES-REASSIGN-001: rejected_driver_ids ist ein Audit-/Auto-Auswahl-Signal — die AUSDRUECKLICHE
  // manuelle Partnerwahl bietet historisch ablehnende Fahrer wieder an. Nur der AKTUELL
  // angenommene bzw. aktuell zugewiesene Fahrer bleibt aus der Neuauswahl ausgeblendet.
  const selectableDriversForRide = (ride) => drivers.filter((driver) => driver.id !== ride.driver_id
    && driver.id !== ride.assigned_driver_id);

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '8px 4px' }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Reservierungen</h2>
      <p style={{ color: '#4b5563', fontSize: 14, marginBottom: 20 }}>
        Reservierungen in deiner Region. Nimm innerhalb des Zeitfensters an – sonst geht die Fahrt an andere Partner.
      </p>
      {refreshNotice&&<p role="status">{refreshNotice}</p>}
      {(loadError || actionError) && <div role="alert" style={{ ...card, borderColor: '#fca5a5', color: '#991b1b' }}>
        {actionError || loadError}
      </div>}
      <button onClick={load} style={{ ...btn('#6b7280', true), marginBottom: 14 }}>Aktualisieren</button>

      {/* RESERVIERUNG-GRUND-ZEIGEN-001 — „niemand kommt in Frage" mit dem WARUM daneben.
          Gruppiert je Grund, sortiert nach dem, was der Partner selbst beheben kann.
          Keine Fahrernamen, keine Fahrer-IDs — nur Anzahlen aus dem eigenen Betrieb. */}
      {noReach && (
        <div
          data-testid="no-reach-panel"
          style={{ ...card, borderColor: '#fca5a5', background: '#fef2f2', marginBottom: 20 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#991b1b' }}>
                {noReach.eligible === 0
                  ? 'Freigegeben — aber kein Fahrer kommt dafür in Frage'
                  : 'Freigegeben — aber kein aktives Fahrergerät verfügbar'}
              </div>
              <div style={{ fontSize: 13, color: '#7f1d1d', marginTop: 4 }}>
                Die Reservierung bleibt freigegeben und kann von berechtigten Fahrern geladen werden. Die Pushanzeige auf dem Gerät ist nicht bestätigt.
              </div>
            </div>
            <button
              onClick={() => setNoReach(null)}
              aria-label="Hinweis schließen"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', padding: 2 }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
            {noReach.groups.map((g) => (
              <div
                key={g.code}
                style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 10, padding: '9px 11px' }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: .3 }}>
                  {REMEDY_LABEL[g.remedy]}
                </div>
                <div style={{ fontSize: 13, color: '#0a0a0a', marginTop: 2 }}>
                  <strong>{g.count === 1 ? '1 Fahrer' : `${g.count} Fahrer`}</strong> {g.text}
                </div>
                {g.hint && (
                  <div style={{ fontSize: 12, color: '#4b5563', marginTop: 3 }}>→ {g.hint}</div>
                )}
              </div>
            ))}
            {noReach.groups.length === 0 && (
              <div style={{ fontSize: 13, color: '#7f1d1d' }}>
                {noReach.fleetDriverCount === 0
                  ? 'In deinem Betrieb ist noch kein Fahrer angelegt.'
                  : 'Zu dieser Freigabe liegt gerade kein Grund je Fahrer vor.'}
              </div>
            )}
            {noReach.note && (
              <div style={{ fontSize: 12, color: '#7f1d1d' }}>{noReach.note}</div>
            )}
          </div>
        </div>
      )}

      {chains.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '8px 0' }}>🔗 Ketten-Vorschläge <span style={{ color: '#82468c' }}>({chains.length})</span></h3>
          <p style={{ color: '#4b5563', fontSize: 13, marginBottom: 8 }}>Zusammenpassende Reservierungen — lückenlos für EINEN Fahrer (Fahrtdauer + Anfahrt + Puffer geprüft).</p>
          {chains.map(k => {
            const ckey = k.rides.join('|');
            return (
              <div key={ckey} style={{ ...card, borderColor: '#ce93d8', background: '#faf5fc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#82468c' }}>Kette · {k.fahrten} Fahrten</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563' }}>{fmtWhen(k.startZeit)} – {uhrzeit(k.endeZeit)} Uhr · {euro(k.total_price)}</div>
                </div>
                <div style={{ margin: '8px 0' }}>
                  {k.legs.map((leg, i) => (
                    <div key={leg.id} style={{ fontSize: 13, color: '#374151', padding: '6px 0', borderTop: i ? '1px solid #f0e6f5' : 'none' }}>
                      <div style={{ marginBottom: 4 }}>
                        <b>{uhrzeit(leg.scheduled_for)}</b>
                        {leg.duration_min ? ` · ${leg.duration_min} Min` : ''}
                      </div>
                      {routeLine(leg)}
                    </div>
                  ))}
                </div>
                {assignChainKey === ckey ? (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Ganze Kette an Fahrer:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {drivers.length === 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>Keine freigegebenen Fahrer.</span>}
                      {drivers.map(d => <button key={d.id} disabled={assigningChainKey === ckey}
                        onClick={() => assignChain(k, d.id, ckey)}
                        style={{ ...btn('#82468c'), opacity: assigningChainKey === ckey ? 0.55 : 1 }}>{d.name}</button>)}
                      <button onClick={() => setAssignChainKey(null)} style={btn('#6b7280', true)}>Abbrechen</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAssignChainKey(ckey)} style={btn('#82468c')}><Users size={14} /> Ganze Kette einem Fahrer zuweisen</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '8px 0' }}>
        Neue Anfragen {(offered.length + offeredBundles.length) > 0
          && <span style={{ color: '#0a0a0a' }}>({offered.length + offeredBundles.length})</span>}
      </h3>
      {offered.length === 0 && offeredBundles.length === 0
        && <div style={{ ...card, color: '#6b7280', fontSize: 14 }}>Aktuell keine neuen Reservierungen.</div>}
      {offeredBundles.map(bundle => {
        const key = `partner-offer:${bundle.bundle_id}`;
        const disabled = !liveOffer(bundle.partner_offer_expires_at) || assigningChainKey === key;
        return (
          <div key={bundle.bundle_id} data-partner-offer-bundle={bundle.bundle_id}
            style={{ ...card, borderColor: '#ce93d8', background: '#faf5fc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#82468c' }}>Kette · {bundle.count} Fahrten</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563' }}>
                Partnerantwort {countdown(bundle.partner_offer_expires_at)} · {euro(bundle.total_price)}
              </div>
            </div>
            <div style={{ margin: '8px 0' }}>
              {(bundle.members || []).map((member, index) => (
                <div key={member.id} style={{ padding: '4px 0', borderTop: index ? '1px solid #f0e6f5' : 'none' }}>
                  <b style={{ fontSize: 13 }}>{uhrzeit(member.scheduled_for)}</b>
                  {routeLine(member)}
                </div>
              ))}
            </div>
            {assignChainKey === key ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {drivers.length === 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>Keine freigegebenen Fahrer.</span>}
                {drivers.map(driver => (
                  <button key={driver.id} disabled={disabled} onClick={() => assignChain({
                    rides: (bundle.members || []).map(member => member.id),
                    state_token: bundle.state_token,
                  }, driver.id, key)} style={{ ...btn('#82468c'), opacity: assigningChainKey === key ? 0.55 : 1 }}>{driver.name}</button>
                ))}
                <button onClick={() => setAssignChainKey(null)} style={btn('#6b7280', true)}>Abbrechen</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button disabled={disabled} onClick={() => setAssignChainKey(key)} style={btn('#82468c')}>
                  <Users size={14} /> Ganze Kette einem Fahrer zuweisen
                </button>
                <button disabled={disabled} onClick={() => declineBundle(bundle)} style={btn('#dc2626', true)}>
                  <X size={14} /> Ganze Kette ablehnen
                </button>
              </div>
            )}
          </div>
        );
      })}
      {offered.map(r => {
        const selectableDrivers = selectableDriversForRide(r);
        const open = r.open_market === true;
        const disabled = (!open && !liveOffer(r.partner_offer_expires_at)) || assigningChainKey === `single:${r.id}`;
        return (
        <div key={r.id} data-partner-offer-single={r.id} data-open-market={open ? 'true' : undefined} style={{ ...card, borderColor: open ? '#0a0a0a' : '#d1d5db' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}><Calendar size={14} style={{ verticalAlign: -2 }} /> {fmtWhen(r.scheduled_for)}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#4b5563' }}>{open ? 'Offen für alle – wer zuerst annimmt' : `Partnerantwort ${countdown(r.partner_offer_expires_at)}`}</div>
          </div>
          {open && <div style={{ fontSize: 12, color: '#4b5563', margin: '4px 0' }}>Diese Reservierung ist für alle Partner und Fahrer freigegeben. Nimmst du an, gehört sie dir.</div>}
          {routeLine(r)}
          <div style={{ fontSize: 13, color: '#4b5563' }}>{r.customer_name} · {euro(r.price)} · {paymentLabels.get(r.payment_method) || 'Zahlungsart nicht bestätigt'}</div>
          {assignFor === r.id ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Fahrer wählen:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {selectableDrivers.length === 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>Keine weiteren freigegebenen Fahrer.</span>}
                {selectableDrivers.map(d => <button key={d.id} disabled={disabled} onClick={() => accept(r, d.id)} style={btn('#111')}>{d.name}</button>)}
                <button onClick={() => setAssignFor(null)} style={btn('#6b7280', true)}>Abbrechen</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button disabled={disabled} onClick={() => accept(r, null)} style={btn('#0a0a0a')}><Users size={14} /> An alle Fahrer</button>
              <button disabled={disabled} onClick={() => setAssignFor(r.id)} style={btn('#111')}><User size={14} /> Fahrer wählen</button>
              {!open && <button disabled={disabled} onClick={() => decline(r)} style={btn('#dc2626', true)}><X size={14} /> Ablehnen</button>}
            </div>
          )}
        </div>
        );
      })}

      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '22px 0 8px' }}>Angenommen</h3>
      {acceptedBundles.map(bundle => {
        const key = `bundle:${bundle.bundle_id}`;
        const selectableDrivers = drivers.filter(driver => driver.id !== bundle.driver_id
          && driver.id !== bundle.assigned_driver_id);
        const status = driverStatus(bundle);
        return (
          <div key={bundle.bundle_id} data-reservation-bundle={bundle.bundle_id}
            style={{ ...card, borderColor: '#ce93d8', background: '#faf5fc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#82468c' }}>Kette · {bundle.count} Fahrten</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563' }}>
                {fmtWhen(bundle.from)} – {bundle.to ? `${uhrzeit(bundle.to)} Uhr` : '—'} · {euro(bundle.total_price)}
              </div>
            </div>
            <div style={{ margin: '8px 0' }}>
              {(bundle.members || []).map((member, index) => (
                <div key={member.id} style={{ padding: '4px 0', borderTop: index ? '1px solid #f0e6f5' : 'none' }}>
                  <b style={{ fontSize: 13 }}>{uhrzeit(member.scheduled_for)}</b>
                  {routeLine(member)}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: bundle.driver_id ? '#0a0a0a' : '#4b5563', margin: '6px 0' }}>{status}</div>
            {assignmentNote(bundle)}
            {assignChainKey === key ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {selectableDrivers.length === 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>Keine weiteren freigegebenen Fahrer.</span>}
                {selectableDrivers.map(driver => (
                  <button key={driver.id} disabled={assigningChainKey === key} onClick={() => assignChain({
                    rides: (bundle.members || []).map(member => member.id),
                    state_token: bundle.state_token,
                  }, driver.id, key)} style={{ ...btn('#82468c'), opacity: assigningChainKey === key ? 0.55 : 1 }}>{driver.name}</button>
                ))}
                <button onClick={() => setAssignChainKey(null)} style={btn('#6b7280', true)}>Abbrechen</button>
              </div>
            ) : (
              <button onClick={() => setAssignChainKey(key)} style={btn('#82468c')}>
                <User size={14} /> Bundle neu zuweisen
              </button>
            )}
          </div>
        );
      })}
      {acceptedSingles.length === 0 && acceptedBundles.length === 0 && <div style={{ ...card, color: '#6b7280', fontSize: 14 }}>Noch keine angenommenen Reservierungen.</div>}
      {acceptedSingles.map(r => {
        const selectableDrivers = selectableDriversForRide(r);
        const status = driverStatus(r);
        const disabled = assigningChainKey === `single:${r.id}`;
        const sc = r.driver_id ? '#0a0a0a' : (r.assigned_driver_id || r.released_to_fleet) ? '#4b5563' : '#dc2626';
        return (
        <div key={r.id} data-reservation-single={r.id} style={card}>
          <div style={{ fontSize: 16, fontWeight: 700 }}><Calendar size={14} style={{ verticalAlign: -2 }} /> {fmtWhen(r.scheduled_for)}</div>
          {routeLine(r)}
          <div style={{ fontSize: 13, color: '#4b5563' }}>{r.customer_name} · {euro(r.price)}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: sc, margin: '6px 0' }}>{status}</div>
          {assignmentNote(r)}
          {assignFor === r.id ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectableDrivers.length === 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>Keine weiteren freigegebenen Fahrer.</span>}
              {selectableDrivers.map(d => <button key={d.id} disabled={disabled} onClick={() => accept(r, d.id)} style={btn('#111')}>{d.name}</button>)}
              <button onClick={() => setAssignFor(null)} style={btn('#6b7280', true)}>Abbrechen</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button disabled={disabled} onClick={() => accept(r, null)} style={btn('#0a0a0a')}><Users size={14} /> {r.released_to_fleet ? 'Erneut an alle' : 'An alle Fahrer'}</button>
              <button disabled={disabled} onClick={() => setAssignFor(r.id)} style={btn('#111')}><User size={14} /> {(r.assigned_driver_id || r.driver_id) ? 'Fahrer neu wählen' : 'Fahrer wählen'}</button>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}
